import { Button, CircularProgress } from "@mui/material";
import { Toast } from "shared-ui/src/components/Toast";
import { useEffect } from "react";
import { DateTime } from "luxon";
import DownloadIcon from "@mui/icons-material/Download";
import {
  GetLifecycleEventLineItemsSearchRequest,
  LifecycleManagementAllEvents,
} from "@lib/ShiOneClient";
import useGridExcelExport from "../../../../hooks/useGridExcelExport";
import { IExcelDownloadDefinitionWithFormat } from "@features/assets-feature/helpers/excelGenerationHelpers";
import MuiDataGrid from "@basecomponents/mui-data-grid/MuiDataGrid";
import { useGetAssetLifecycleManagementDrawerLineItemsData } from "./hooks/useGetAssetLifecycleManagementDrawerLineItemsData";
import { LifecycleEventDetailsGrid } from "shared-ui/src/components/lifecycle-management/lifecycle-event-details";
import type { LifecycleEventLineItem } from "shared-ui/src/components/lifecycle-management/lifecycle-event-details";

const LIFECYCLE_LINE_ITEMS_EXPORT_DEFINITIONS: IExcelDownloadDefinitionWithFormat[] =
  [
    { header: "Account", dataField: "account" },
    { header: "Mfg Part #", dataField: "manufacturerPartNumber" },
    { header: "Product Description", dataField: "productDescription" },
    { header: "Quantity", dataField: "quantity" },
    { header: "Category", dataField: "category" },
    { header: "Event Type", dataField: "eventType" },
    { header: "Event Date", dataField: "eventDate" },
    { header: "Order Date", dataField: "orderDate" },
    { header: "PO Number", dataField: "poNumber" },
    { header: "Contract Number", dataField: "contractNumber" },
    { header: "Previous Purchase Cost", dataField: "previousPurchaseCost", numFmt: "$#,##0.00" },
    { header: "Serial Number", dataField: "serialNumber" },
  ];

const AssetLifecycleManagementDrawerLineItems = ({
  selectedLifecycleEventData,
  onSaveHandlerChange,
}: {
  selectedLifecycleEventData: LifecycleManagementAllEvents | undefined;
  onSaveHandlerChange?: (handler: (() => void) | undefined) => void;
}) => {
  useEffect(() => {
    onSaveHandlerChange?.(undefined);
  }, [onSaveHandlerChange]);

  const { data, isLoading, isError, refetch } =
    useGetAssetLifecycleManagementDrawerLineItemsData({
      uniqueIdentifiers: selectedLifecycleEventData?.allUniqueIdentifiers || [],
    } as unknown as GetLifecycleEventLineItemsSearchRequest);

  const lineItems: LifecycleEventLineItem[] = (data?.lineItems ?? []).map(
    (item) => ({
      groupId: item.groupId ?? undefined,
      account: item.account ?? undefined,
      manufacturerPartNumber: item.manufacturerPartNumber ?? undefined,
      productDescription: item.productDescription ?? undefined,
      serialNumber: item.serialNumber ?? undefined,
      quantity: item.quantity ?? undefined,
      category: item.category ?? undefined,
      eventType: item.eventType ?? undefined,
      eventDate: item.eventDate ?? undefined,
      orderDate: item.orderDate ?? undefined,
      poNumber: item.poNumber ?? undefined,
      soNumber: item.soNumber ?? undefined,
      contractNumber: item.contractNumber ?? undefined,
      previousPurchaseCost: item.previousPurchaseCost ?? undefined,
      groupQuantity: item.groupQuantity ?? undefined,
      groupPreviousPurchaseCost: item.groupPreviousPurchaseCost ?? undefined,
    }),
  );

  const total = {
    orders: data?.total?.orders ?? 0,
    lineItems: data?.total?.lineItems ?? 0,
    previousPurchaseCost: data?.total?.previousPurchaseCost ?? 0,
  };

  const { isExporting, handleExport } = useGridExcelExport({
    rows: lineItems,
    definitions: LIFECYCLE_LINE_ITEMS_EXPORT_DEFINITIONS,
    filename: () =>
      `Lifecycle-Line-Items-${DateTime.now().toFormat("MM-dd-yyyy")}`,
    onError: (error) => {
      Toast(error instanceof Error ? error.message : "Export failed", true);
      console.error("Line items export failed:", error);
    },
    onEmpty: () => Toast("No line items available to export", true),
  });

  return (
    <LifecycleEventDetailsGrid
      lineItems={lineItems}
      total={total}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      renderHeaderActions={() => (
        <Button
          data-testid="export-button"
          variant="outlined"
          color="inherit"
          startIcon={
            isExporting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleExport}
          disabled={isExporting || isLoading}
        >
          Export
        </Button>
      )}
      renderGrid={({
        columnDefinitions,
        rows,
        isLoading: loading,
        hiddenColumns,
        treeData,
      }) => (
        <MuiDataGrid
          gridDefinitions={{ columnDefinitions, hiddenColumns }}
          gridData={rows}
          loading={loading}
          checkboxSelection={false}
          getRowId={(row) => row._rowId}
          treeData={treeData}
          sx={{
            "& .MuiDataGrid-cell.MuiDataGrid-cellEmpty": { padding: 0 },
          }}
        />
      )}
    />
  );
};

export default AssetLifecycleManagementDrawerLineItems;
